/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Menu, PLACEMENT, Popup } from '@web-stories-wp/design-system';

const MenuLayer = styled.div`
  z-index: 12;
`;

const MoreButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:focus {
    outline: 2px solid #76a0f0;
    outline-offset: 2px;
  }
`;

const TEMPLATE_ACTION_MENU_WIDTH = 190;
const VIEWPORT_EDGE_PADDING = 8;

const menuStylesCSS = css`
  min-width: ${TEMPLATE_ACTION_MENU_WIDTH}px;
  margin-top: 0;

  li {
    grid-template-columns: 1fr;
  }

  li > span {
    grid-column-start: 1;
  }

  li span {
    white-space: nowrap;
  }
`;

function TemplateActionMenu({ ariaLabel, items, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState(PLACEMENT.BOTTOM_END);
  const buttonRef = useRef();
  const menuId = useMemo(() => uuidv4(), []);
  const buttonId = `template-action-button-${menuId}`;
  const listId = `template-action-menu-${menuId}`;

  const options = useMemo(
    () => [
      {
        group: items,
      },
    ],
    [items]
  );

  const stopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const updatePlacement = useCallback(() => {
    const buttonRect = buttonRef.current?.getBoundingClientRect();

    if (!buttonRect) {
      return;
    }

    const wouldOverflowLeft =
      buttonRect.right - TEMPLATE_ACTION_MENU_WIDTH < VIEWPORT_EDGE_PADDING;

    setPlacement(
      wouldOverflowLeft ? PLACEMENT.BOTTOM_START : PLACEMENT.BOTTOM_END
    );
  }, []);

  const handleDismiss = useCallback((event) => {
    const dismissSource =
      event?.target ||
      event?.srcElement ||
      event?.nativeEvent?.target ||
      event?.srcElement?.nearestViewportElement;

    if (
      dismissSource &&
      typeof dismissSource === 'object' &&
      'nodeType' in dismissSource &&
      buttonRef.current?.contains(dismissSource)
    ) {
      return;
    }

    setIsOpen(false);
  }, []);

  const handleMenuItemClick = useCallback(
    (event, value) => {
      event.stopPropagation();
      setIsOpen(false);
      onSelect(value);
    },
    [onSelect]
  );

  return (
    <>
      <MoreButton
        ref={buttonRef}
        type="button"
        onMouseDown={stopPropagation}
        onMouseEnter={updatePlacement}
        onClick={(event) => {
          event.stopPropagation();
          updatePlacement();
          setIsOpen((current) => !current);
        }}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={listId}
        id={buttonId}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z"
            fill="black"
            fillOpacity="0.55"
          />
          <path
            d="M7 12C7 11.7476 7.08985 11.5316 7.26956 11.3519C7.44926 11.1722 7.66529 11.0824 7.91764 11.0824C8.16999 11.0824 8.38602 11.1722 8.56573 11.3519C8.74543 11.5316 8.83528 11.7476 8.83528 12C8.83528 12.2524 8.74543 12.4684 8.56573 12.6481C8.38602 12.8278 8.16999 12.9176 7.91764 12.9176C7.66529 12.9176 7.44926 12.8278 7.26956 12.6481C7.08985 12.4684 7 12.2524 7 12ZM11.0824 12C11.0824 11.7476 11.1722 11.5316 11.3519 11.3519C11.5316 11.1722 11.7476 11.0824 12 11.0824C12.2524 11.0824 12.4684 11.1722 12.6481 11.3519C12.8278 11.5316 12.9176 11.7476 12.9176 12C12.9176 12.2524 12.8278 12.4684 12.6481 12.6481C12.4684 12.8278 12.2524 12.9176 12 12.9176C11.7476 12.9176 11.5316 12.8278 11.3519 12.6481C11.1722 12.4684 11.0824 12.2524 11.0824 12ZM15.1647 12C15.1647 11.7476 15.2546 11.5316 15.4343 11.3519C15.614 11.1722 15.83 11.0824 16.0824 11.0824C16.3347 11.0824 16.5507 11.1722 16.7304 11.3519C16.9101 11.5316 17 11.7476 17 12C17 12.2524 16.9101 12.4684 16.7304 12.6481C16.5507 12.8278 16.3347 12.9176 16.0824 12.9176C15.83 12.9176 15.614 12.8278 15.4343 12.6481C15.2546 12.4684 15.1647 12.2524 15.1647 12Z"
            fill="white"
          />
        </svg>
      </MoreButton>
      <Popup
        anchor={buttonRef}
        placement={placement}
        isOpen={isOpen}
        fillWidth={false}
      >
        <MenuLayer onMouseDown={stopPropagation} onClick={stopPropagation}>
          <Menu
            parentId={buttonId}
            listId={listId}
            options={options}
            onMenuItemClick={handleMenuItemClick}
            onDismissMenu={handleDismiss}
            hasMenuRole
            menuStylesOverride={menuStylesCSS}
          />
        </MenuLayer>
      </Popup>
    </>
  );
}

TemplateActionMenu.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default TemplateActionMenu;
